import { useEffect, useRef, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { normalizarCodigoColado, CODIGO_DIGITOS } from '../lib/codigo';

/**
 * Tela de acesso ao Back Office.
 *
 * Existe porque o produto subiu sem NENHUMA porta de entrada: o HTML respondia
 * 200 e toda chamada devolvia 401, então quem abria via só "Autenticação
 * necessária". Rota entregue sem caminho na tela que a alcance não está pronta.
 *
 * Duas etapas: e-mail e depois código. A UX de teclado é do playbook e não é
 * enfeite — usuário real reclama de ter que clicar no botão:
 *
 *   - Enter submete AS DUAS etapas;
 *   - autofocus no campo ativo de cada etapa;
 *   - `autocomplete="one-time-code"` no código, para o preenchimento automático
 *     do celular funcionar;
 *   - colar é sanitizado (só dígitos, cortado no tamanho), porque colar de um
 *     e-mail traz espaço, quebra de linha e às vezes o texto em volta.
 */

type Etapa = { readonly nome: 'email' } | { readonly nome: 'codigo'; readonly email: string };

export function Login({ aoEntrar }: { readonly aoEntrar: () => void }) {
  const [etapa, setEtapa] = useState<Etapa>({ nome: 'email' });
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const campoEmail = useRef<HTMLInputElement>(null);
  const campoCodigo = useRef<HTMLInputElement>(null);

  // Foco no campo ATIVO de cada etapa. Sem isto a pessoa precisa clicar depois
  // de cada passo, que é exatamente a fricção que o playbook manda remover.
  useEffect(() => {
    if (etapa.nome === 'email') campoEmail.current?.focus();
    else campoCodigo.current?.focus();
  }, [etapa.nome]);

  async function pedirCodigo(evento: React.FormEvent) {
    evento.preventDefault();
    if (enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      const resposta = await api.requestOtp(email.trim());
      // A resposta é IDÊNTICA para e-mail que existe e que não existe — é isso
      // que impede descobrir quem tem conta. A tela repete essa mesma frase em
      // vez de afirmar que o código foi enviado.
      setAviso(resposta.message);
      setEtapa({ nome: 'codigo', email: email.trim() });
    } catch (e) {
      setErro(
        e instanceof ApiError ? e.message : 'Não foi possível falar com o plano de controle.',
      );
    } finally {
      setEnviando(false);
    }
  }

  async function verificarCodigo(evento: React.FormEvent) {
    evento.preventDefault();
    if (enviando || etapa.nome !== 'codigo') return;
    setEnviando(true);
    setErro(null);
    try {
      await api.verifyOtp(etapa.email, codigo);
      aoEntrar();
    } catch (e) {
      // A API responde a MESMA mensagem para código errado, expirado e
      // tentativas esgotadas. A tela não inventa distinção que o servidor não
      // faz — inventar seria vazar em qual dos casos a pessoa está.
      setErro(e instanceof ApiError ? e.message : 'Não foi possível verificar o código.');
      setCodigo('');
      campoCodigo.current?.focus();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <main className="mx-auto max-w-sm p-6 text-sm">
      <h1 className="text-lg font-semibold">Ferrari Instagram AI</h1>
      <p className="mt-1 text-xs text-ink-faint">Plano de controle: acesso restrito</p>

      {etapa.nome === 'email' ? (
        <form className="mt-6 space-y-3" onSubmit={pedirCodigo}>
          <label className="block" htmlFor="login-email">
            E-mail
          </label>
          <input
            id="login-email"
            ref={campoEmail}
            type="email"
            required
            autoComplete="email"
            className="w-full rounded border px-3 py-2"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button
            type="submit"
            disabled={enviando || email.trim().length < 3}
            className="w-full rounded bg-ink px-3 py-2 text-surface disabled:opacity-50"
          >
            {enviando ? 'Enviando…' : 'Receber código'}
          </button>
        </form>
      ) : (
        <form className="mt-6 space-y-3" onSubmit={verificarCodigo}>
          <p className="text-xs text-ink-faint">
            Código enviado para <strong>{etapa.email}</strong>
          </p>
          <label className="block" htmlFor="login-codigo">
            Código de {CODIGO_DIGITOS} dígitos
          </label>
          <input
            id="login-codigo"
            ref={campoCodigo}
            type="text"
            required
            inputMode="numeric"
            /* O preenchimento automatico do celular depende deste valor exato. */
            autoComplete="one-time-code"
            maxLength={CODIGO_DIGITOS}
            className="w-full rounded border px-3 py-2 tracking-widest"
            value={codigo}
            onChange={(e) => setCodigo(normalizarCodigoColado(e.target.value))}
            onPaste={(e) => {
              // Colar passa pelo MESMO saneamento do digitar. Deixar o paste
              // cru entrar faria "  123 456 " virar um codigo invalido.
              e.preventDefault();
              setCodigo(normalizarCodigoColado(e.clipboardData.getData('text')));
            }}
          />
          <button
            type="submit"
            disabled={enviando || codigo.length !== CODIGO_DIGITOS}
            className="w-full rounded bg-ink px-3 py-2 text-surface disabled:opacity-50"
          >
            {enviando ? 'Verificando…' : 'Entrar'}
          </button>
          <button
            type="button"
            className="w-full rounded border px-3 py-2"
            onClick={() => {
              setEtapa({ nome: 'email' });
              setCodigo('');
              setErro(null);
              setAviso(null);
            }}
          >
            Usar outro e-mail
          </button>
        </form>
      )}

      {aviso ? (
        <p className="mt-4 text-xs text-ink-faint" role="status">
          {aviso}
        </p>
      ) : null}
      {erro ? (
        <p className="mt-4 text-xs text-danger-ink" role="alert">
          {erro}
        </p>
      ) : null}
    </main>
  );
}
